import { VisibilityElement } from '../custom-elements/visibility'
import { NativeFetcher } from './fetch'
import { globalState } from './utils'
import { CartRequestItem, Cart as CartType, ChangeItem, UpdateCartRequest } from './cart.types'
import { PWP } from './product-with-purchase'

interface CustomCartEvent extends Event {
  detail: {
    count?: number
    action?: boolean
  }
}

interface ShopifySectionEvent extends Event {
  detail: {
    sectionId?: string
    blockId?: string
    load?: boolean
  }
}

export class Cart extends NativeFetcher {
  // eslint-disable-next-line no-use-before-define
  static instance: Cart | null = null

  // Node IDs
  static ID__MINI_CART = 'mini-cart'

  // DOM Elements IDS
  static CLASS__CART_COUNT = '.cart-count'
  static DATA__CART_COUNT = 'data-item-count'

  // Endpoints
  static ENDPOINT__GET_CART = globalState?.routes?.cart_url + '?view=ajax'
  static ENDPOINT__CLEAR_CART = globalState?.routes?.cart_clear_url + '.js'
  static ENDPOINT__ADD_LINE_ITEMS = globalState?.routes?.cart_add_url + '.js'
  static ENDPOINT__UPDATE_LINE_ITEMS = globalState?.routes?.cart_update_url + '.js'
  static ENDPOINT__CHANGE_LINE_ITEM = globalState?.routes?.cart_change_url + '.js'

  static SHOPIFY__SECTION_SELECT = 'shopify:section:select'
  static SHOPIFY__SECTION_ID = /sections--\d+__mini-cart$/

  // Instance variables
  value: CartType | undefined // The actual cart

  constructor() {
    if (Cart.instance) return Cart.instance
    super()
    Cart.instance = this
    /** Add Event Listeners */
    this.addEventListeners()
    /** On initialisation get the cart from shopify
     * set the instance variable 'value' with the current
     * customer cart.
     */
    this.getCart()
    this.value = globalState?.cart?.initCart as CartType || undefined
  }

  // Static Cart Handlers
  static EVENT_UPDATE_CART_COUNT = 'updateCartCount'
  static dispatchUpdateCartCount(count: number) {
    document.dispatchEvent(
      new CustomEvent(Cart.EVENT_UPDATE_CART_COUNT, {
        detail: {
          count
        }
      })
    )
  }

  // Static Cart Handlers
  static EVENT_RENDER_MINI_CART = 'renderMiniCart'
  static EVENT_EXTERNAL_OPEN_MINI_CART = 'externalOpenMiniCart'
  static dispatchRenderMiniCart(action: boolean) {
    document.dispatchEvent(
      new CustomEvent(Cart.EVENT_RENDER_MINI_CART, {
        detail: {
          action
        }
      })
    )
  }

  addEventListeners() {
    document.addEventListener(Cart.EVENT_UPDATE_CART_COUNT, this.handleCartCount)
    document.addEventListener(Cart.EVENT_RENDER_MINI_CART, this.handleMiniCartRender)
    document.addEventListener(Cart.SHOPIFY__SECTION_SELECT, this.handleSectionSelect)
    window.addEventListener(Cart.EVENT_EXTERNAL_OPEN_MINI_CART, () => {
      Cart.dispatchRenderMiniCart(true)
      Cart.dispatchOpenMiniCart()
    })
  }

  handleSectionSelect(event: Event) {
    const customEvent = event as ShopifySectionEvent
    const { sectionId } = customEvent.detail
    if (!sectionId) return
    const match = sectionId.match(Cart.SHOPIFY__SECTION_ID)
    if (!match) {
      Cart.dispatchHideMiniCart()
      return
    }
    Cart.dispatchOpenMiniCart()
  }

  static dispatchOpenMiniCart() {
    VisibilityElement.dispatchShow(Cart.ID__MINI_CART)
  }

  dispatchOpenMiniCart() {
    Cart.dispatchOpenMiniCart()
  }

  static dispatchHideMiniCart() {
    VisibilityElement.dispatchHide(Cart.ID__MINI_CART)
  }

  dispatchHideMiniCart() {
    Cart.dispatchHideMiniCart()
  }

  // API CALLS - 1:1 with Shopify Docs

  async getCart() {
    this.value = await this.get(Cart.ENDPOINT__GET_CART)
    if (this.value) {
      Cart.dispatchUpdateCartCount(this.value?.item_count || 0)
    }
    return this.value
  }

  /**
   * Adds line item to cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */
  async addLineItems(items: CartRequestItem[], options = { silent: false }) {
    const { silent = false } = options

    const body = {
      items: items.length ? items : [items]
    }

    const data = await this.post(Cart.ENDPOINT__ADD_LINE_ITEMS, body)
    if (data && !silent && !data.error) {
      const addedItems = Array.isArray(data.items) ? data.items : [data]
      const addedItem = addedItems[addedItems.length - 1]
      const updatedCart = await this.getCart()
      const enrichedItem = updatedCart?.items?.find(
        (line) => String(line.variant_id) === String(addedItem?.variant_id)
      )
      PWP.showProductWithPurchase({ ...addedItem, ...enrichedItem }, updatedCart)
      Cart.dispatchOpenMiniCart()
    }
    Cart.dispatchRenderMiniCart(true)

    return data
  }

  /**
   * Update line item in cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   */
  async updateLineItems(items: UpdateCartRequest['updates'], options = { silent: true }) {
    const { silent = true } = options

    const body = {
      updates: items
    }
    const data = await this.post(Cart.ENDPOINT__UPDATE_LINE_ITEMS, body)
    if (data && !silent) Cart.dispatchOpenMiniCart()
    Cart.dispatchUpdateCartCount(data.item_count)
    return data
  }

  /**
   * Changes line item in cart asynchronously
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async changeLineItem(item: ChangeItem, options = { silent: true }) {
    const { silent = true } = options

    const data = await this.post(Cart.ENDPOINT__CHANGE_LINE_ITEM, item)
    if (data && !silent && !data.error) {
      Cart.dispatchOpenMiniCart()
    }
    Cart.dispatchUpdateCartCount(data.item_count)
    Cart.dispatchRenderMiniCart(true)
    return data
  }

  /**
   * Clears Cart
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async clearCart(options = { silent: true }) {
    const { silent = true } = options

    const data = await this.post(Cart.ENDPOINT__CLEAR_CART, {})
    if (data && !silent) Cart.dispatchOpenMiniCart()
    return data
  }

  // Auxillary API handlers - wrappers for additional common cases

  async removeLineItem(id: string | number, options: { silent: boolean }) {
    const items = {
      [id]: 0
    }
    return await this.updateLineItems(items, options)
  }

  async updateLineItemQuantity(item: CartRequestItem, options: { silent: boolean }) {
    const { id, quantity } = item
    const items = {
      [id]: quantity
    }

    return await this.updateLineItems(items, options)
  }

  handleCartCount(event: Event) {
    const customEvent = event as CustomCartEvent
    const cartCountDOMElements = document.querySelectorAll(Cart.CLASS__CART_COUNT)
    if (!cartCountDOMElements.length) return
    /** Get item count  */
    const { count } = customEvent.detail
    if (count === undefined) {
      console.error('Cart count not provided')
      return
    }

    for (let i = 0; i < cartCountDOMElements.length; i++) {
      const cartCountDOMElement = cartCountDOMElements[i]
      /** Check if cart count is hidden */
      cartCountDOMElement.setAttribute(Cart.DATA__CART_COUNT, count.toString())

      /** Apply new item count  */
      cartCountDOMElement.innerHTML = `${count}`
    }
  }

  /**
   * Handles the mini-cart render
   * The mini-cart preact component contains a useUrl hook
   * that will re-render the component when the url changes updates.
   *
   */
  handleMiniCartRender() {
    const url = new URL(window.location.href)
    /** Add the action query param.
     * 'action=1' has no purpose other than a placeholder value
     * when triggering the mini-cart preact component.
     */
    url.searchParams.set('action', '1')
    // Push the new URL to history
    window.history.pushState({}, '', url.toString())

    /** Push back to trigger the popstate event listener. */
    window.history.back()
  }
}

export const cart = new Cart()
